"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "McpService", {
    enumerable: true,
    get: function() {
        return McpService;
    }
});
const _nodepath = /*#__PURE__*/ _interop_require_default(require("node:path"));
const _common = require("@nestjs/common");
const _streamableHttp = require("@modelcontextprotocol/sdk/server/streamableHttp.js");
const _mcp = require("@modelcontextprotocol/sdk/server/mcp.js");
const _zod = require("zod");
const _nocodbsdk = require("nocodb-sdk");
const _attachmentHelpers = require("~/helpers/attachmentHelpers");
const _basesv3service = require("~/services/v3/bases-v3.service");
const _tablesv3service = require("~/services/v3/tables-v3.service");
const _datav3service = require("~/services/v3/data-v3.service");
const _datatableservice = require("~/services/data-table.service");
const _roleHelper = require("~/utils/roleHelper");
const _NcPluginMgrv2 = /*#__PURE__*/ _interop_require_default(require("~/helpers/NcPluginMgrv2"));
const _serialize = require("~/helpers/serialize");
const _auditsservice = require("~/services/audits.service");
const _utils = require("~/utils");
const _descriptions = require("~/mcp/descriptions");
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let McpService = class McpService {
    async handleRequest(tokenId, context, req, res) {
        const server = new _mcp.McpServer({
            name: `NoocDB MCP Server`,
            version: '1.0.0'
        });
        await this.registerTools({
            context,
            user: req.user,
            server,
            req
        });
        const transport = new _streamableHttp.StreamableHTTPServerTransport({
            sessionIdGenerator: undefined
        });
        res.on('close', ()=>{
            transport.close();
            server.close();
        });
        await server.connect(transport);
        await transport.handleRequest(req, res, req.body);
    }
    async registerTools({ server, context, user, req }) {
        const isEditorPlus = (0, _roleHelper.hasMinimumRole)(user, _nocodbsdk.ProjectRoles.EDITOR);
        // Base Details
        server.registerTool('getBaseInfo', {
            title: 'Get Base Info',
            description: 'Fetch information about current base',
            annotations: {
                readOnlyHint: true,
                idempotentHint: true
            }
        }, async ()=>{
            try {
                const baseInfo = await this.baseV3Service.getProject(context, {
                    baseId: context.base_id
                });
                return {
                    content: [
                        {
                            type: 'text',
                            text: JSON.stringify(baseInfo, null, 2)
                        }
                    ]
                };
            } catch (error) {
                return {
                    content: [
                        {
                            type: 'text',
                            text: `Error: ${error.message}`
                        }
                    ],
                    isError: true
                };
            }
        });
        // List Tables
        server.registerTool('getTablesList', {
            title: 'List Tables',
            annotations: {
                readOnlyHint: true,
                idempotentHint: true
            },
            description: 'List tables accessible by user'
        }, async ()=>{
            try {
                const tables = await this.tablesV3Service.getAccessibleTables(context, {
                    baseId: context.base_id,
                    roles: (0, _nocodbsdk.extractRolesObj)(user == null ? void 0 : user.base_roles),
                    user,
                    allSources: true
                });
                return {
                    content: [
                        {
                            type: 'text',
                            text: JSON.stringify(tables, null, 2)
                        }
                    ]
                };
            } catch (error) {
                return {
                    content: [
                        {
                            type: 'text',
                            text: `Error: ${error.message}`
                        }
                    ],
                    isError: true
                };
            }
        });
        // Get Table Schema
        server.registerTool('getTableSchema', {
            title: 'Get the table schema',
            description: 'Get the table schema including fields and views information',
            inputSchema: {
                tableId: _zod.z.string().describe('Table Id')
            },
            annotations: {
                readOnlyHint: true,
                idempotentHint: true
            }
        }, async ({ tableId })=>{
            try {
                const table = await this.tablesV3Service.getTableWithAccessibleViews(context, {
                    tableId,
                    user
                });
                if (!table) {
                    return {
                        content: [
                            {
                                type: 'text',
                                text: `Error: Table "${tableId}" not found`
                            }
                        ],
                        isError: true
                    };
                }
                return {
                    content: [
                        {
                            type: 'text',
                            text: JSON.stringify(table, null, 2)
                        }
                    ]
                };
            } catch (error) {
                return {
                    content: [
                        {
                            type: 'text',
                            text: `Error: ${error.message}`
                        }
                    ],
                    isError: true
                };
            }
        });
        // Query Records
        server.registerTool('queryRecords', {
            title: 'Query Records',
            description: 'Query Records from a Table',
            inputSchema: {
                tableId: _zod.z.string().describe('Table ID'),
                pageSize: _zod.z.number().optional().describe('Number of records to fetch (default: 50)'),
                page: _zod.z.number().optional().describe('Page number for pagination (default: 1)'),
                where: _zod.z.string().optional().describe(_descriptions.whereDescription),
                sort: _zod.z.array(_zod.z.object({
                    field: _zod.z.string().describe('Field Name'),
                    description: _zod.z.enum([
                        'asc',
                        'desc'
                    ]).describe('Sort Direction')
                })).optional(),
                fields: _zod.z.array(_zod.z.string()).optional().describe('Fields to fetch').optional()
            },
            annotations: {
                readOnlyHint: true
            }
        }, async ({ tableId, pageSize = 50, page = 1, where, sort, fields })=>{
            try {
                pageSize = Math.max(1, Math.min(pageSize || 25, 200));
                // Prepare parameters
                const params = {
                    pageSize,
                    page
                };
                if (where) params.where = where;
                if (sort) params.sort = sort;
                if (fields) params.fields = fields;
                const records = await this.datasV3Service.dataList(context, {
                    baseId: context.base_id,
                    modelId: tableId,
                    query: params,
                    req: req
                });
                return {
                    content: [
                        {
                            type: 'text',
                            text: JSON.stringify(records, null, 2)
                        }
                    ]
                };
            } catch (error) {
                return {
                    content: [
                        {
                            type: 'text',
                            text: `Error: ${error.message}`
                        }
                    ],
                    isError: true
                };
            }
        });
        // Get Record by ID tool
        server.registerTool('getRecord', {
            title: 'Get Record',
            description: 'Fetch a record by ID',
            inputSchema: {
                tableId: _zod.z.string().describe('Table ID'),
                recordId: _zod.z.string().describe('Record ID or primary key value'),
                fields: _zod.z.string().optional().describe('Comma-separated list of fields to include')
            },
            annotations: {
                readOnlyHint: true
            }
        }, async ({ tableId, recordId, fields })=>{
            try {
                const params = {};
                if (fields) params.fields = fields;
                const record = await this.dataTableService.dataRead(context, {
                    modelId: tableId,
                    rowId: recordId,
                    baseId: context.base_id,
                    apiVersion: _nocodbsdk.NcApiVersion.V3,
                    query: params
                });
                return {
                    content: [
                        {
                            type: 'text',
                            text: JSON.stringify(record, null, 2)
                        }
                    ]
                };
            } catch (error) {
                return {
                    content: [
                        {
                            type: 'text',
                            text: `Error: ${error.message}`
                        }
                    ],
                    isError: true
                };
            }
        });
        server.registerTool('countRecords', {
            title: 'Count Records',
            description: 'Count Records in a Table',
            inputSchema: {
                tableId: _zod.z.string().describe('Table ID'),
                where: _zod.z.string().optional().describe(_descriptions.whereDescription)
            },
            annotations: {
                readOnlyHint: true
            }
        }, async ({ tableId, where })=>{
            try {
                const params = {};
                if (where) params.where = where;
                const count = await this.dataTableService.dataCount(context, {
                    baseId: context.base_id,
                    modelId: tableId,
                    query: params,
                    apiVersion: _nocodbsdk.NcApiVersion.V3
                });
                return {
                    content: [
                        {
                            type: 'text',
                            text: JSON.stringify(count, null, 2)
                        }
                    ]
                };
            } catch (error) {
                return {
                    content: [
                        {
                            type: 'text',
                            text: `Error: ${error.message}`
                        }
                    ],
                    isError: true
                };
            }
        });
        server.registerTool('readAttachment', {
            title: 'Read Attachments',
            description: 'Read attachments in a record',
            inputSchema: {
                files: _zod.z.array(_zod.z.object({
                    title: _zod.z.string().nullable().describe('Attachment title'),
                    mimeType: _zod.z.string().nullable().describe('Attachment mime type'),
                    size: _zod.z.number().nullable().describe('Attachment size')
                }).and(_zod.z.union([
                    _zod.z.object({
                        url: _zod.z.string().nullable().describe('Attachment URL. Required if `path` is not provided.'),
                        signedUrl: _zod.z.string().nullable().describe('Attachment signed URL. Required if `path` is not provided.'),
                        path: _zod.z.null(),
                        signedPath: _zod.z.null()
                    }),
                    _zod.z.object({
                        path: _zod.z.string().nullable().describe('Attachment path. Required if `url` is not provided.'),
                        signedPath: _zod.z.string().nullable().describe('Attachment signed Path. Required if `url` is not provided.'),
                        url: _zod.z.null(),
                        signedUrl: _zod.z.null()
                    })
                ]))).describe('Array of attachment objects from NocoDB')
            },
            annotations: {
                readOnlyHint: true
            }
        }, async ({ files })=>{
            try {
                if (!files || files.length === 0) {
                    return {
                        content: [
                            {
                                type: 'text',
                                text: 'Error: No attachments provided'
                            }
                        ],
                        isError: true
                    };
                }
                const storageAdapter = await _NcPluginMgrv2.default.storageAdapter();
                const results = await Promise.all(files.map(async (file)=>{
                    try {
                        let relativePath;
                        // Determine the relative path from attachment
                        if (file.path) {
                            relativePath = _nodepath.default.join('nc', 'uploads', file.path.replace(/^download[/\\]/i, ''));
                        } else if (file.url) {
                            relativePath = (0, _attachmentHelpers.getPathFromUrl)(file.url).replace(/^\/+/, '');
                        } else {
                            return {
                                title: file.title || 'Unknown file',
                                error: 'No path or URL available for this attachment'
                            };
                        }
                        const stream = await storageAdapter.fileReadByStream(relativePath);
                        if (!stream) {
                            return {
                                title: file.title || 'Unknown file',
                                error: 'Failed to read file stream'
                            };
                        }
                        const mimeType = file.mimeType || 'application/octet-stream';
                        const serialized = await (0, _serialize.serialize)(mimeType, stream, `Could not process file: ${file.title || 'Unknown file'}`);
                        const hasContent = serialized.text && serialized.text !== '@file_not_supported';
                        return {
                            title: file.title || 'Unknown file',
                            mimeType,
                            size: file.size,
                            content: hasContent ? serialized.text : null,
                            images: serialized.images,
                            error: hasContent ? null : 'Could not extract text from this file type'
                        };
                    } catch (error) {
                        return {
                            title: file.title || 'Unknown file',
                            error: `Error processing file: ${error.message}`
                        };
                    }
                }));
                // Compile all content into one response
                const successfulResults = results.filter((r)=>r.content);
                const failedResults = results.filter((r)=>r.error);
                // Format content for the response
                let responseText = '';
                if (successfulResults.length > 0) {
                    responseText += '## Successfully Processed Files\n\n';
                    for (const result of successfulResults){
                        responseText += `### ${result.title}\n`;
                        responseText += `**Type:** ${result.mimeType}\n`;
                        responseText += `**Size:** ${formatFileSize(result.size)}\n\n`;
                        responseText += `${result.content}\n\n`;
                        if (result.images && result.images.length > 0) {
                            responseText += `*This file contains ${result.images.length} images that cannot be directly displayed in text format.*\n\n`;
                        }
                    }
                }
                if (failedResults.length > 0) {
                    responseText += '## Files With Processing Issues\n\n';
                    for (const result of failedResults){
                        responseText += `### ${result.title}\n`;
                        responseText += `**Error:** ${result.error}\n\n`;
                    }
                }
                return {
                    content: [
                        {
                            type: 'text',
                            text: responseText.trim()
                        }
                    ]
                };
            } catch (error) {
                return {
                    content: [
                        {
                            type: 'text',
                            text: `Error: ${error.message}`
                        }
                    ],
                    isError: true
                };
            }
        });
        if (!_utils.isEE) {
            server.registerTool('aggregate_single', {
                title: 'Aggregate',
                description: 'Perform aggregations on a table with a filter condition',
                annotations: {
                    readOnlyHint: true,
                    idempotentHint: true
                },
                inputSchema: {
                    tableId: _zod.z.string().describe('Table ID'),
                    aggregations: _zod.z.array(_zod.z.object({
                        field: _zod.z.string().describe('Field/column ID to aggregate'),
                        type: _zod.z.enum([
                            // Numerical aggregations
                            'sum',
                            'min',
                            'max',
                            'avg',
                            'median',
                            'std_dev',
                            'range',
                            // Common aggregations
                            'count',
                            'count_empty',
                            'count_filled',
                            'count_unique',
                            'percent_empty',
                            'percent_filled',
                            'percent_unique',
                            // Boolean aggregations
                            'checked',
                            'unchecked',
                            'percent_checked',
                            'percent_unchecked',
                            // Date aggregations
                            'earliest_date',
                            'latest_date',
                            'date_range',
                            'month_range',
                            // None
                            'none'
                        ]).describe(_descriptions.aggregationDescription)
                    })).describe('Array of aggregations to perform'),
                    where: _zod.z.string().optional().describe(_descriptions.whereDescription),
                    viewId: _zod.z.string().optional().describe('Optional view ID to use view-specific configurations')
                }
            }, async ({ aggregations, tableId, where, viewId })=>{
                try {
                    const result = await this.dataTableService.dataAggregate(context, {
                        modelId: tableId,
                        viewId: viewId,
                        query: {
                            where: where,
                            aggregation: JSON.stringify(aggregations)
                        }
                    });
                    return {
                        content: [
                            {
                                type: 'text',
                                text: JSON.stringify(result, null, 2)
                            }
                        ],
                        isError: false
                    };
                } catch (error) {
                    return {
                        content: [
                            {
                                type: 'text',
                                text: `Error: ${error.message}`
                            }
                        ],
                        isError: true
                    };
                }
            });
        }
        if (isEditorPlus) {
            // Create Records tool
            server.registerTool('createRecords', {
                title: 'Create Records',
                description: 'Create records in a table',
                annotations: {
                    readOnlyHint: true,
                    idempotentHint: true
                },
                inputSchema: {
                    tableId: _zod.z.string().describe('Table ID'),
                    records: _zod.z.array(_zod.z.object({
                        fields: _zod.z.record(_zod.z.string().describe('Field name/title'), _zod.z.any().describe('Field value'))
                    })).describe('Array of records with fields as key-value pairs')
                }
            }, async ({ tableId, records })=>{
                try {
                    const recordsArray = Array.isArray(records) ? records : [
                        records
                    ];
                    const result = await this.datasV3Service.dataInsert(context, {
                        modelId: tableId,
                        baseId: context.base_id,
                        body: recordsArray,
                        cookie: req
                    });
                    return {
                        content: [
                            {
                                type: 'text',
                                text: JSON.stringify(result, null, 2)
                            }
                        ]
                    };
                } catch (error) {
                    return {
                        content: [
                            {
                                type: 'text',
                                text: `Error: ${error.message}`
                            }
                        ],
                        isError: true
                    };
                }
            });
            // Update Records tool
            server.registerTool('updateRecords', {
                title: 'Update Records',
                description: 'Update records in a table',
                inputSchema: {
                    tableId: _zod.z.string().describe('Table ID'),
                    records: _zod.z.array(_zod.z.object({
                        id: _zod.z.union([
                            _zod.z.string(),
                            _zod.z.number()
                        ]).describe('Record ID'),
                        fields: _zod.z.record(_zod.z.string().describe('Field name/title'), _zod.z.any().describe('Field value'))
                    })).describe('Array of records with ID and fields to update')
                },
                annotations: {
                    destructiveHint: true
                }
            }, async ({ tableId, records })=>{
                try {
                    const recordsArray = Array.isArray(records) ? records : [
                        records
                    ];
                    const result = await this.datasV3Service.dataUpdate(context, {
                        modelId: tableId,
                        baseId: context.base_id,
                        body: recordsArray,
                        cookie: req
                    });
                    return {
                        content: [
                            {
                                type: 'text',
                                text: JSON.stringify(result, null, 2)
                            }
                        ]
                    };
                } catch (error) {
                    return {
                        content: [
                            {
                                type: 'text',
                                text: `Error: ${error.message}`
                            }
                        ],
                        isError: true
                    };
                }
            });
            // Delete Records tool
            server.registerTool('deleteRecords', {
                title: 'Delete Records',
                description: 'Delete records in a table',
                annotations: {
                    destructiveHint: true
                },
                inputSchema: {
                    tableId: _zod.z.string().describe('Table ID'),
                    records: _zod.z.array(_zod.z.object({
                        id: _zod.z.union([
                            _zod.z.string(),
                            _zod.z.number()
                        ]).describe('Record ID')
                    })).describe('Array of records with IDs to delete')
                }
            }, async ({ tableId, records })=>{
                try {
                    const recordsArray = Array.isArray(records) ? records : [
                        records
                    ];
                    const result = await this.datasV3Service.dataDelete(context, {
                        modelId: tableId,
                        baseId: context.base_id,
                        body: recordsArray,
                        cookie: req
                    });
                    return {
                        content: [
                            {
                                type: 'text',
                                text: JSON.stringify(result, null, 2)
                            }
                        ]
                    };
                } catch (error) {
                    return {
                        content: [
                            {
                                type: 'text',
                                text: `Error: ${error.message}`
                            }
                        ],
                        isError: true
                    };
                }
            });
        }
    }
    constructor(baseV3Service, tablesV3Service, datasV3Service, dataTableService, auditService){
        this.baseV3Service = baseV3Service;
        this.tablesV3Service = tablesV3Service;
        this.datasV3Service = datasV3Service;
        this.dataTableService = dataTableService;
        this.auditService = auditService;
    }
};
McpService = _ts_decorate([
    (0, _common.Injectable)(),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof _basesv3service.BasesV3Service === "undefined" ? Object : _basesv3service.BasesV3Service,
        typeof _tablesv3service.TablesV3Service === "undefined" ? Object : _tablesv3service.TablesV3Service,
        typeof _datav3service.DataV3Service === "undefined" ? Object : _datav3service.DataV3Service,
        typeof _datatableservice.DataTableService === "undefined" ? Object : _datatableservice.DataTableService,
        typeof _auditsservice.AuditsService === "undefined" ? Object : _auditsservice.AuditsService
    ])
], McpService);
function formatFileSize(bytes) {
    if (bytes === undefined || bytes === null) return 'Unknown size';
    if (bytes < 1024) return `${bytes} bytes`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}
